import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import LiveEventPageSkeleton from '@om-fe/core/pages/theme1/live-event-page-skeleton';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import { DisciplineIcon } from '@om-fe/widgets/dist/components/icon/discipline-icon';
import { FlagIcon, FlagIconLocations } from '@om-fe/widgets/dist/components/icon/flag-icon';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { useLang } from '@om-fe/widgets/dist/common/i18n/i18n.hooks';
import { RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks';
import { FavoriteButton } from '@om-fe/core/components/favorites/favorite.utils';
import { BemMethod, createBem } from '@om-fe/utils';
import { compose } from 'redux';
import { MatchDetailsViewModel, DisciplineLiveStatus, DisciplineModel, TournamentModel } from '@om-fe/models';
import { connect } from 'react-redux';
import getSlice from '@om-fe/core/utils/get-slice';
import { AppState } from '@om-fe/core/core/types';
import { MatchDetailsStateExt } from '@om-fe/core/components/match-details/match-details.store';
import {
    SLICE_LIVE_MATCH_DETAILS, subscribeToLiveMatchDetails, unsubscribeLiveMatchDetails
} from './mobile-header-live-event-page.store';
import { GetBreadcrumbsResponse } from '@om-fe/core/components/breadcrumbs/breadcrumbs.constants';
import { isRacingDiscipline } from '@om-fe/core/components/sport-parent/sport-parent.utils'
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { getTournament } from '@om-fe/api';
import BackButton from '../back-button/back-button';

const breadcrumbsBem: BemMethod = createBem('Breadcrumbs');

interface MobileHeaderLiveEventPagePublicProps {
    readonly bem: BemMethod,
    readonly toggleDrawer: () => void,
    readonly isDrawerOpen: boolean,
    readonly matchId: string,
    readonly disciplineId: string;
    readonly routeName: RouteNames;
}

interface MobileHeaderLiveEventPageStoreProps {
    readonly event?: MatchDetailsViewModel;
}

type MobileHeaderLiveEventPageProps = MobileHeaderLiveEventPagePublicProps
    & MobileHeaderLiveEventPageStoreProps

function MobileHeaderLiveEventPageComponent (
    {
        toggleDrawer,
        isDrawerOpen,
        event,
        matchId,
        disciplineId,
        routeName,
    }: MobileHeaderLiveEventPageProps
) {
    
    const [breadcrumbsData, setBreadcrumbsData] = useState<TournamentModel | undefined>(undefined);
    const lang: string = useLang();
    const params = useMemo(() => ({
        lang,
        liveStatus: DisciplineLiveStatus.LIVE,
        sportId: disciplineId,
        maxResults: 1,
    }), [lang, disciplineId]);
    const [{ data, status }] = useRpcState<GetBreadcrumbsResponse<DisciplineModel>>('disciplines', params);
    const showEventCategory: boolean | undefined = status === RpcStateStatus.Success && data?.records[0]?.showEventCategory ? true : false;
    const showIcon: boolean = true;

    useEffect(() => {
        const tournamentData = async () => {
            const data: TournamentModel | undefined = await getTournament(matchId, lang);
            setBreadcrumbsData(data);
        };
        !event && tournamentData();
    },[matchId, lang, event, disciplineId]);

    const getTournamentBreadcrumbs = useCallback(() => {
        if (!breadcrumbsData) {
            return null;
        }
        
        return(
            <>
                {showIcon && (
                    <div className={breadcrumbsBem('Flags')}>
                        <DisciplineIcon disciplineId={breadcrumbsData.sportId}  />
                        <FlagIcon locationId={isRacingDiscipline(breadcrumbsData.sportId) ? FlagIconLocations.World : breadcrumbsData.venueId} />
                    </div>
                )}
                <span className={breadcrumbsBem('EventName')}>
                    <span className={breadcrumbsBem('ParticipantName')}>{breadcrumbsData.shortTranslatedName}</span>
                </span>
            </>
        );
    },[breadcrumbsData, showIcon]);

    return (
        <div className={breadcrumbsBem()} onClick={toggleDrawer}>
            {event
                ?
                    <>
                        {showIcon && (
                            <>
                                <BackButton isLarge={false} className={breadcrumbsBem('BackButton')}/>
                                <div className={breadcrumbsBem('Flags')}>
                                    <DisciplineIcon disciplineId={event.disciplineId} />
                                    <FlagIcon locationId={event.locationId} />
                                </div>
                            </>
                        )}
                        <span className={breadcrumbsBem('EventName')}>
                            <span className={breadcrumbsBem('ParticipantName')}>
                                {showEventCategory ? `${event?.categoryName} / ${event?.tournamentName}` : event?.tournamentName}
                            </span>
                        </span>
                    </>
                : routeName === RouteNames.liveOutright && getTournamentBreadcrumbs()
            }
            <GeneralIcon name={isDrawerOpen ? 'arr-up' : 'arr-down'} />
            <FavoriteButton matchId={matchId} />
        </div>
    );
};

export const MobileHeaderLiveEventPage: FC<MobileHeaderLiveEventPagePublicProps> = compose(
    preLoader<MobileHeaderLiveEventPageProps>({
        preload({ props, dispatch }) {
            const { matchId } = props;
            dispatch(subscribeToLiveMatchDetails(matchId));
        },
        update({prevProps, props, dispatch}) {
            const { matchId } = props;
            const { matchId: prevMacthId} = prevProps;

            if (matchId && prevMacthId && matchId !== prevMacthId) {
                dispatch(unsubscribeLiveMatchDetails(prevMacthId));
                dispatch(subscribeToLiveMatchDetails(matchId));
            }
        },
        getReadySlice({}: MobileHeaderLiveEventPageProps): string {
            return `${SLICE_LIVE_MATCH_DETAILS}.ready`;
        },
        destroy: ({ props, dispatch }) => {
            const { matchId } = props;

            if (matchId) {
                dispatch(unsubscribeLiveMatchDetails(matchId));
            }
        },
        loadingAnimation: LiveEventPageSkeleton,
    }),
    connect(
        (state: AppState) => {
            const liveMatchSlice: MatchDetailsStateExt | undefined = getSlice(state, SLICE_LIVE_MATCH_DETAILS);
            const event: MatchDetailsViewModel | undefined = liveMatchSlice?.event

            if (!liveMatchSlice) {
                return {}
            }


            return {
                event
            };
        }
    )
)(MobileHeaderLiveEventPageComponent)
